import { cn } from '@qr-front/ui/utils'
import { RhcField } from 'features/generator/components/RhcField'
import { qrTypeAtom } from 'features/generator/generatorState'
import { QR_TYPE } from 'features/generator/generatorTypes'
import { useAtomValue } from 'jotai'
import { useTranslations } from 'next-intl'
import { chain } from 'radash'
import { useFormContext } from 'react-hook-form'

import { filterChars, maxLength } from '@/utils'

import {
	addressCityAtom,
	addressCountryAtom,
	addressPostalCodeAtom,
	addressStateAtom,
	addressStreetAtom,
} from '../addressAtoms'
import {
	CITY_MAX_LENGTH,
	COUNTRY_MAX_LENGTH,
	POSTAL_CODE_MAX_LENGTH,
	STATE_MAX_LENGTH,
	STREET_MAX_LENGTH,
	TAddressFields,
} from '../addressSchema'

export const FullAddressFields = () => {
	const t = useTranslations()
	const qrType = useAtomValue(qrTypeAtom)
	const isBusinessPageQrType = QR_TYPE.BusinessPage === qrType

	const form = useFormContext<TAddressFields>()

	const streetField = (
		<RhcField
			type="text"
			form={form}
			path="address.street"
			fieldName="address_street"
			atom={addressStreetAtom}
			onChangeTransform={(event) => maxLength(STREET_MAX_LENGTH)(event)}
			label={t(
				'generator__content_form_section__about__address_section__street',
			)}
			placeholder={t(
				'generator__content_form_section__about__address_section__street__placeholder',
			)}
			className="col-span-2 tablet:col-span-1"
		/>
	)

	const postalCodeField = (
		<RhcField
			type="text"
			form={form}
			path="address.postalCode"
			fieldName="address_postal_code"
			atom={addressPostalCodeAtom}
			onChangeTransform={(event) =>
				chain(
					filterChars('[a-zA-Z0-9 -]'),
					maxLength(POSTAL_CODE_MAX_LENGTH),
				)(event)
			}
			label={t(
				'generator__content_form_section__about__address_section__postal_code',
			)}
			placeholder={t(
				'generator__content_form_section__about__address_section__postal_code__placeholder',
			)}
			className="col-span-2 tablet:col-span-1"
		/>
	)

	const cityField = (
		<RhcField
			type="text"
			form={form}
			path="address.city"
			fieldName="address_city"
			atom={addressCityAtom}
			onChangeTransform={(event) => maxLength(CITY_MAX_LENGTH)(event)}
			label={t(
				'generator__content_form_section__about__address_section__city',
			)}
			placeholder={t(
				'generator__content_form_section__about__address_section__city__placeholder',
			)}
			className="col-span-2 tablet:col-span-1"
		/>
	)

	const stateField = (
		<RhcField
			type="text"
			form={form}
			path="address.state"
			fieldName="address_state"
			atom={addressStateAtom}
			onChangeTransform={(event) => maxLength(STATE_MAX_LENGTH)(event)}
			label={t(
				'generator__content_form_section__about__address_section__state',
			)}
			placeholder={t(
				'generator__content_form_section__about__address_section__state__placeholder',
			)}
			className="col-span-2 tablet:col-span-1"
		/>
	)

	const countryField = (
		<RhcField
			type="text"
			form={form}
			path="address.country"
			fieldName="address_country"
			atom={addressCountryAtom}
			onChangeTransform={(event) => maxLength(COUNTRY_MAX_LENGTH)(event)}
			label={t(
				'generator__content_form_section__about__address_section__country',
			)}
			placeholder={t(
				'generator__content_form_section__about__address_section__country__placeholder',
			)}
			className="col-span-2"
		/>
	)

	return (
		<div
			className={cn(
				'grid grid-cols-2 gap-y-4',
				isBusinessPageQrType ? 'gap-x-12' : 'gap-x-8',
			)}
		>
			{streetField}
			{postalCodeField}
			{cityField}
			{stateField}
			{countryField}
		</div>
	)
}
